/** Admin New Quote dialog — full parity with the public booking form:
 *  vehicle cards, transfer/hourly tabs, round trip, add-on counters,
 *  custom add-ons, live auto-pricing + preserved manual overrides. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";
import api from "@/lib/api";
import { Loader2, Sparkles, Minus, Plus, X, Repeat, Car, Clock } from "lucide-react";

const FIELD =
  "bg-[#0a0a0a] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-[#C5A059]";

const DEFAULT_VEHICLE_CARDS = [
  { key: "standard", label: "Comfort",        sub: "Sedan · up to 4",            vehicle_type: "Comfort Class",            mult: 1.00 },
  { key: "xl",       label: "XL Vehicle",     sub: "SUV · up to 6",              vehicle_type: "XL Vehicle (6+1)",         mult: 1.15 },
  { key: "van",      label: "Van",            sub: "Van · up to 8",              vehicle_type: "Van (8+1)",                mult: 1.30 },
  { key: "business", label: "Business Class", sub: "Mercedes E-Class · up to 3", vehicle_type: "Business Class (E-Class)", mult: 1.80 },
  { key: "vip_van",  label: "VIP Luxury Van", sub: "Mercedes V-Class · up to 7", vehicle_type: "VIP Luxury Van (V-Class)", mult: 2.20 },
];

const ADDONS = [
  { code: "beverage_water",      label: "Chilled Water",        price: 3.0 },
  { code: "beverage_ice_coffee", label: "Chilled Ice Coffee",   price: 5.0 },
  { code: "beverage_cola",       label: "Chilled Cola",         price: 5.0 },
  { code: "beverage_beer",       label: "Chilled Local Beer",   price: 5.0 },
  { code: "beverage_energy",     label: "Chilled Energy Drink", price: 5.0 },
  { code: "snack_box",           label: "Premium Snack Box",    price: 13.0 },
  { code: "sim_card",            label: "Tourist 5G SIM Card",  price: 20.0 },
];

const HOURS = [2, 3, 4, 5, 6, 7, 8];
const WAITS = [0, 1, 2, 3, 4, 6, 8];

export default function NewQuoteDialog({ open, onOpenChange, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [cards, setCards] = useState(DEFAULT_VEHICLE_CARDS);
  const [f, setF] = useState(initial());

  // Sync multipliers with the Admin Pricing Dashboard.
  useEffect(() => {
    if (!open) return;
    api.get("/pricing/catalog").then(({ data }) => {
      const byKey = Object.fromEntries((data?.categories || []).map((c) => [c.key, c]));
      setCards(DEFAULT_VEHICLE_CARDS.map((c) => byKey[c.key]
        ? { ...c, mult: Number(byKey[c.key].multiplier) || c.mult }
        : c));
    }).catch(() => {});
  }, [open]);

  function initial() {
    return {
      guest_name: "", guest_email: "", guest_phone: "",
      pickup: "", dropoff: "",
      pickup_coords: null, dropoff_coords: null,
      pickup_date: "", pickup_time: "",
      passengers: 2, luggage: 0,
      vehicle_category: "standard",
      booking_type: "transfer",
      duration_hours: 2,
      is_round_trip: false, return_date: "", return_time: "", waiting_hours: 0,
      upsells: {}, custom_addons: [],
      distance_km: "", duration_min: "",
      total_price_eur: "", notes: "",
      deposit_mode: "default", deposit_value: "", remaining_payment_location: "",
    };
  }

  useEffect(() => { if (open) { setF(initial()); setEstimate(null); } }, [open]);

  const update = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isHourly = f.booking_type === "hourly";
  const selCard = cards.find((v) => v.key === f.vehicle_category) || cards[0];
  const vehLabel = selCard.vehicle_type;

  const catUpsellsPayload = () =>
    Object.entries(f.upsells).filter(([, q]) => q > 0).map(([code, qty]) => ({ code, qty }));
  const customTotal = f.custom_addons.reduce(
    (a, x) => a + (Number(x.qty) || 0) * (Number(x.price) || 0), 0);

  // ---- live auto-estimate (same engine as the public form) ----
  const upsellsKey = JSON.stringify(f.upsells);
  useEffect(() => {
    const ready = isHourly
      ? f.pickup && Number(f.duration_hours) >= 2
      : f.pickup && f.dropoff;
    if (!open || !ready) { setEstimate(null); return; }
    const t = setTimeout(async () => {
      setEstimating(true);
      try {
        const { data } = await api.post("/quote", {
          pickup_location: f.pickup,
          dropoff_location: isHourly ? "" : f.dropoff,
          pickup_coords: f.pickup_coords || null,
          dropoff_coords: isHourly ? null : (f.dropoff_coords || null),
          pickup_time: f.pickup_time || "",
          luggage: Number(f.luggage) || 0,
          booking_type: f.booking_type,
          duration_hours: Number(f.duration_hours) || 0,
          is_round_trip: !isHourly && !!f.is_round_trip,
          waiting_hours: Number(f.waiting_hours) || 0,
          upsells: catUpsellsPayload(),
          vehicle_category: f.vehicle_category,
          vehicle_type: "Comfort Class",
        });
        setEstimate(data);
      } catch { setEstimate(null); }
      finally { setEstimating(false); }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, f.pickup, f.dropoff, f.pickup_coords, f.dropoff_coords, f.pickup_time,
      f.vehicle_category, f.booking_type, f.duration_hours, f.is_round_trip,
      f.waiting_hours, f.luggage, upsellsKey]);

  const autoTotal = estimate ? +(estimate.total_price_eur + customTotal).toFixed(2) : null;

  const setQty = (code, qty) => setF((s) => {
    const next = { ...s.upsells };
    if (qty <= 0) delete next[code]; else next[code] = qty;
    return { ...s, upsells: next };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.guest_name || !f.pickup) return toast.error("Guest name and pickup are required.");
    if (!isHourly && !f.dropoff) return toast.error("Drop-off is required for transfers.");
    if (!isHourly && f.is_round_trip && (!f.return_date || !f.return_time)) {
      return toast.error("Round trip needs a return date & time.");
    }
    setSubmitting(true);
    try {
      const payload = {
        guest_name: f.guest_name,
        guest_email: f.guest_email || undefined,
        guest_phone: f.guest_phone,
        pickup: f.pickup,
        dropoff: isHourly ? "" : f.dropoff,
        pickup_coords: f.pickup_coords || undefined,
        dropoff_coords: isHourly ? undefined : (f.dropoff_coords || undefined),
        pickup_date: f.pickup_date,
        pickup_time: f.pickup_time,
        passengers: Number(f.passengers) || 1,
        luggage: Number(f.luggage) || 0,
        vehicle_type: vehLabel,
        vehicle_category: f.vehicle_category,
        booking_type: f.booking_type,
        duration_hours: isHourly ? Number(f.duration_hours) : undefined,
        is_round_trip: !isHourly && !!f.is_round_trip,
        return_date: f.return_date, return_time: f.return_time,
        waiting_hours: Number(f.waiting_hours) || 0,
        upsells: [
          ...catUpsellsPayload(),
          ...f.custom_addons
            .filter((x) => x.label.trim() && Number(x.qty) > 0)
            .map((x) => ({ label: x.label.trim(), qty: Number(x.qty), price: Number(x.price) || 0 })),
        ],
        notes: f.notes,
      };
      if (f.distance_km) payload.distance_km = Number(f.distance_km);
      if (f.duration_min) payload.duration_min = Number(f.duration_min);
      if (f.total_price_eur) payload.total_price_eur = Number(f.total_price_eur);
      if (f.deposit_mode && f.deposit_mode !== "default") {
        payload.deposit_mode = f.deposit_mode;
        if (f.deposit_value !== "" && f.deposit_value !== null && f.deposit_mode !== "none") {
          payload.deposit_value = Number(f.deposit_value);
        }
      }
      if (f.remaining_payment_location) payload.remaining_payment_location = f.remaining_payment_location;
      const { data } = await api.post("/admin/quotes", payload);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create quote.");
    } finally {
      setSubmitting(false);
    }
  };

  const pacGuard = (e) => {
    if (e.target && e.target.closest && e.target.closest(".pac-container")) e.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-quote-dialog"
        className="bg-[#0d0d0d] border-white/10 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={pacGuard}
        onInteractOutside={pacGuard}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white font-light">New Quote</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Auto-calculates from vehicle, route, duration, waiting time & add-ons. Override anything manually.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 mt-2">
          {/* Vehicle cards */}
          <div data-testid="q-vehicle-cards" className="grid grid-cols-3 gap-2">
            {cards.map((v) => {
              const active = f.vehicle_category === v.key;
              return (
                <button
                  key={v.key} type="button"
                  data-testid={`q-vehicle-${v.key}`}
                  onClick={() => update("vehicle_category", v.key)}
                  className={`rounded-xl border p-3 text-left transition ${
                    active
                      ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                      : "border-white/10 text-zinc-400 hover:border-[#C5A059]/40"
                  }`}
                >
                  <Car className={`w-4 h-4 mb-1.5 ${active ? "text-[#C5A059]" : "text-zinc-500"}`} />
                  <div className="text-sm font-medium">{v.label}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{v.sub}</div>
                  <div className={`text-[9px] font-mono mt-1 ${active ? "text-[#C5A059]" : "text-zinc-600"}`}>×{v.mult.toFixed(2)}</div>
                </button>
              );
            })}
          </div>

          {/* Service mode tabs */}
          <div data-testid="q-mode-tabs" className="grid grid-cols-2 gap-1 p-1 rounded-full border border-white/10 bg-black/40">
            {[["transfer", "Transfer"], ["hourly", "Hourly Charter"]].map(([mode, label]) => (
              <button
                key={mode} type="button"
                data-testid={`q-mode-${mode}`}
                onClick={() => update("booking_type", mode)}
                className={`h-9 rounded-full text-sm transition flex items-center justify-center gap-1.5 ${
                  f.booking_type === mode ? "bg-[#C5A059] text-black font-semibold" : "text-zinc-400 hover:text-white"
                }`}
              >
                {mode === "hourly" && <Clock className="w-3.5 h-3.5" />}{label}
              </button>
            ))}
          </div>

          {/* Guest */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Guest Name *">
              <Input data-testid="q-name" value={f.guest_name} onChange={(e) => update("guest_name", e.target.value)} className={FIELD} required />
            </Field>
            <Field label="Guest Email">
              <Input type="email" data-testid="q-email" value={f.guest_email} onChange={(e) => update("guest_email", e.target.value)} className={FIELD} />
            </Field>
            <Field label="Guest Phone">
              <Input data-testid="q-phone" value={f.guest_phone} onChange={(e) => update("guest_phone", e.target.value)} className={FIELD} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Passengers">
                <Input type="number" min={1} max={20} data-testid="q-pax" value={f.passengers} onChange={(e) => update("passengers", e.target.value)} className={FIELD} />
              </Field>
              <Field label="Suitcases">
                <Input type="number" min={0} max={20} data-testid="q-luggage" value={f.luggage} onChange={(e) => update("luggage", e.target.value)} className={FIELD} />
              </Field>
            </div>

            {/* Trip fields */}
            <Field label="Pickup *">
              <PlacesAutocompleteInput
                testId="q-pickup" value={f.pickup}
                onChange={(v) => setF((s) => ({ ...s, pickup: v, pickup_coords: null }))}
                onPlaceSelected={(p) => setF((s) => ({ ...s, pickup: p.name, pickup_coords: { lat: p.lat, lon: p.lon } }))}
                placeholder="Split Airport, Croatia" className={FIELD} required
              />
            </Field>
            {isHourly ? (
              <Field label="Duration *">
                <Select value={String(f.duration_hours)} onValueChange={(v) => update("duration_hours", Number(v))}>
                  <SelectTrigger data-testid="q-hours" className={FIELD}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0d0d0d] border-white/10 text-zinc-100">
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)} data-testid={`q-hours-${h}`}>
                        {h} Hours — €{(h * 60 * (selCard?.mult || 1)).toFixed(0)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : (
              <Field label="Drop-off *">
                <PlacesAutocompleteInput
                  testId="q-dropoff" value={f.dropoff}
                  onChange={(v) => setF((s) => ({ ...s, dropoff: v, dropoff_coords: null }))}
                  onPlaceSelected={(p) => setF((s) => ({ ...s, dropoff: p.name, dropoff_coords: { lat: p.lat, lon: p.lon } }))}
                  placeholder="Hotel Excelsior, Dubrovnik" className={FIELD}
                />
              </Field>
            )}
            <Field label="Date">
              <Input type="date" data-testid="q-date" value={f.pickup_date} onChange={(e) => update("pickup_date", e.target.value)} className={FIELD} />
            </Field>
            <Field label="Time">
              <Input type="time" data-testid="q-time" value={f.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} className={FIELD} />
            </Field>
          </div>

          {/* Round trip */}
          {!isHourly && (
            <div className="rounded-lg border border-white/10 p-3">
              <button
                type="button" data-testid="q-roundtrip-toggle"
                onClick={() => update("is_round_trip", !f.is_round_trip)}
                className="flex items-center gap-2.5 text-sm w-full"
              >
                <span className={`w-9 h-5 rounded-full relative transition ${f.is_round_trip ? "bg-[#C5A059]" : "bg-zinc-700"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform left-0.5 ${f.is_round_trip ? "translate-x-[18px]" : ""}`} />
                </span>
                <Repeat className="w-4 h-4 text-[#C5A059]" />
                <span className={f.is_round_trip ? "text-white" : "text-zinc-400"}>Round trip</span>
                <span className="text-[10px] text-zinc-600 ml-auto">Return leg + waiting at destination</span>
              </button>
              {f.is_round_trip && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <Field label="Return Date">
                    <Input type="date" data-testid="q-return-date" value={f.return_date} onChange={(e) => update("return_date", e.target.value)} className={FIELD} />
                  </Field>
                  <Field label="Return Time">
                    <Input type="time" data-testid="q-return-time" value={f.return_time} onChange={(e) => update("return_time", e.target.value)} className={FIELD} />
                  </Field>
                  <Field label="Wait at Destination">
                    <Select value={String(f.waiting_hours)} onValueChange={(v) => update("waiting_hours", Number(v))}>
                      <SelectTrigger data-testid="q-wait" className={FIELD}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0d0d0d] border-white/10 text-zinc-100">
                        {WAITS.map((w) => (
                          <SelectItem key={w} value={String(w)} data-testid={`q-wait-${w}`}>
                            {w === 0 ? "No wait / under 30 min" : `${w} hour${w > 1 ? "s" : ""}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              )}
            </div>
          )}

          {/* Add-ons */}
          <div className="rounded-lg border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-[#C5A059] mb-3">Enhance your trip</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ADDONS.map((a) => {
                const qty = f.upsells[a.code] || 0;
                return (
                  <div key={a.code} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${qty ? "border-[#C5A059]/50 bg-[#C5A059]/5" : "border-white/10"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-200 truncate">{a.label}</div>
                      <div className="text-[10px] text-zinc-500">€{a.price.toFixed(2)}</div>
                    </div>
                    <button type="button" data-testid={`q-addon-dec-${a.code}`} onClick={() => setQty(a.code, qty - 1)}
                            className="w-6 h-6 rounded-full border border-white/15 text-zinc-400 hover:text-white flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <span data-testid={`q-addon-qty-${a.code}`} className="w-5 text-center text-sm tabular-nums">{qty}</span>
                    <button type="button" data-testid={`q-addon-inc-${a.code}`} onClick={() => setQty(a.code, qty + 1)}
                            className="w-6 h-6 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  </div>
                );
              })}
            </div>
            {/* Custom add-ons */}
            {f.custom_addons.map((x, i) => (
              <div key={i} className="flex items-center gap-2 mt-2 rounded-lg border border-dashed border-[#C5A059]/40 px-3 py-2">
                <Input data-testid={`q-custom-label-${i}`} value={x.label} placeholder="Custom item title"
                       onChange={(e) => setF((s) => { const c = [...s.custom_addons]; c[i] = { ...c[i], label: e.target.value }; return { ...s, custom_addons: c }; })}
                       className={`${FIELD} h-8 text-xs flex-1`} />
                <Input data-testid={`q-custom-price-${i}`} type="number" min="0" step="0.5" value={x.price} placeholder="€"
                       onChange={(e) => setF((s) => { const c = [...s.custom_addons]; c[i] = { ...c[i], price: e.target.value }; return { ...s, custom_addons: c }; })}
                       className={`${FIELD} h-8 text-xs w-20`} />
                <button type="button" onClick={() => setF((s) => { const c = [...s.custom_addons]; c[i] = { ...c[i], qty: Math.max(0, Number(c[i].qty) - 1) }; return { ...s, custom_addons: c }; })}
                        className="w-6 h-6 rounded-full border border-white/15 text-zinc-400 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                <span className="w-5 text-center text-sm tabular-nums" data-testid={`q-custom-qty-${i}`}>{x.qty}</span>
                <button type="button" onClick={() => setF((s) => { const c = [...s.custom_addons]; c[i] = { ...c[i], qty: Number(c[i].qty) + 1 }; return { ...s, custom_addons: c }; })}
                        className="w-6 h-6 rounded-full border border-[#C5A059]/40 text-[#C5A059] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                <button type="button" data-testid={`q-custom-remove-${i}`} onClick={() => setF((s) => ({ ...s, custom_addons: s.custom_addons.filter((_, j) => j !== i) }))}
                        className="text-zinc-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button
              type="button" data-testid="q-custom-add"
              onClick={() => setF((s) => ({ ...s, custom_addons: [...s.custom_addons, { label: "", price: "", qty: 1 }] }))}
              className="mt-2 text-xs text-[#C5A059] hover:text-[#D4AF37] inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Custom Add-on
            </button>
          </div>

          {/* Live estimate */}
          <div data-testid="q-estimate" className="rounded-lg border border-[#C5A059]/30 bg-[#C5A059]/[0.06] px-4 py-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-zinc-400">
              Auto-calculated total {f.total_price_eur ? "(overridden below)" : ""}
            </span>
            <span className="text-lg font-semibold text-[#C5A059] tabular-nums" data-testid="q-estimate-total">
              {estimating ? "…" : autoTotal !== null ? `€${autoTotal.toFixed(2)}` : "—"}
            </span>
          </div>

          {/* Manual override */}
          <div className="rounded-lg border border-dashed border-[#C5A059]/30 bg-[#C5A059]/[0.04] p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-[#C5A059] mb-3">Manual override <span className="text-zinc-500 normal-case tracking-normal">(optional — auto-calculates if left blank)</span></p>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Distance (km)">
                <Input data-testid="q-distance" value={f.distance_km} onChange={(e) => update("distance_km", e.target.value)} className={FIELD} placeholder="auto" />
              </Field>
              <Field label="Duration (min)">
                <Input data-testid="q-duration" value={f.duration_min} onChange={(e) => update("duration_min", e.target.value)} className={FIELD} placeholder="auto" />
              </Field>
              <Field label="Total Price (€)">
                <Input data-testid="q-total" value={f.total_price_eur} onChange={(e) => update("total_price_eur", e.target.value)} className={FIELD} placeholder="auto" />
              </Field>
            </div>
          </div>

          {/* Deposit split */}
          <div className="rounded-lg border border-dashed border-[#C5A059]/30 bg-[#C5A059]/[0.04] p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-[#C5A059] mb-3">
              Deposit split <span className="text-zinc-500 normal-case tracking-normal">(for rentals / experiences — leave default for transfers)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Deposit mode</Label>
                <Select value={f.deposit_mode} onValueChange={(v) => update("deposit_mode", v)}>
                  <SelectTrigger data-testid="q-deposit-mode" className={FIELD}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0d0d0d] border-white/10 text-zinc-100">
                    <SelectItem value="default" data-testid="q-deposit-mode-default">Default (20 %)</SelectItem>
                    <SelectItem value="percent" data-testid="q-deposit-mode-percent">Custom percent</SelectItem>
                    <SelectItem value="absolute" data-testid="q-deposit-mode-absolute">Custom € amount</SelectItem>
                    <SelectItem value="none" data-testid="q-deposit-mode-none">No deposit split</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(f.deposit_mode === "percent" || f.deposit_mode === "absolute") && (
                <Field label={f.deposit_mode === "percent" ? "Deposit (%)" : "Deposit (€)"}>
                  <Input data-testid="q-deposit-value" type="number" min="0" step="0.5" value={f.deposit_value}
                         onChange={(e) => update("deposit_value", e.target.value)}
                         placeholder={f.deposit_mode === "percent" ? "e.g. 17" : "e.g. 60.00"} className={FIELD} />
                </Field>
              )}
              <Field label="Remaining paid to…">
                <Input data-testid="q-remaining-location" value={f.remaining_payment_location}
                       onChange={(e) => update("remaining_payment_location", e.target.value)}
                       placeholder="e.g. Directly to supplier on board" className={FIELD} />
              </Field>
            </div>
          </div>

          <Field label="Notes">
            <Textarea data-testid="q-notes" value={f.notes} onChange={(e) => update("notes", e.target.value)}
                      className="bg-[#0a0a0a] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-[#C5A059] min-h-[80px]"
                      placeholder="Any special instructions for the guest…" />
          </Field>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="flex-1 h-11 rounded-full border border-white/10 text-zinc-300 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
              Cancel
            </button>
            <button
              type="submit" data-testid="q-submit" disabled={submitting}
              className="flex-1 h-11 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Generate Quote</>}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}
